import { useContext, useMemo, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { LootContext } from '../context'
import OptionBar from './OptionBar'
import RcLootCouncilCta from './RcLootCouncilCta'
import Select from 'app/frontend/base/components/Select'
import ItemIcon from 'app/frontend/base/components/ItemIcon'
import { parseItemHash } from './ItemEvaluation'
import TeamWishlistEncounters from './TeamWishlistEncounters'
import TeamWishlistTable from './TeamWishlistTable'
import DifficultySelector from './DifficultySelector'
import { useTeamWishlist } from 'app/frontend/base/config/queries'
import { useTeam } from 'app/frontend/base/config/context/team'
import { Skeleton } from '@/components/ui/skeleton'
import { Clock } from 'lucide-react'
import TeamAgeModal from './TeamAgeModal'
import NoMemberWarning from './NoMemberWarning'
import Card from 'app/frontend/base/components/Card'
import { useHasSidebar } from 'app/frontend/base/hooks/useHasSidebar'
import { cn } from '@/lib/utils'

const TeamOverview = () => {
  const { team } = useTeam()
  const [viewingAge, setViewingAge] = useState(false)
  const { droptimizerConfigurations, selectedSeason, difficulty } =
    useContext(LootContext)
  const { hash } = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const source: 'raid' | 'dungeon' =
    searchParams.get('source') === 'dungeon' ? 'dungeon' : 'raid'
  const [configurationId, setConfigurationId] = useState<string>('all')
  const [selectedItemId, setSelectedItemId] = useState<number | undefined>(() =>
    parseItemHash(hash),
  )
  const [listHash, setListHash] = useState<string | null>(null)
  const [view, setView] = useState<string>(
    parseItemHash(hash)
      ? 'encounters'
      : localStorage.getItem('wishlistOverviewView') || 'encounters',
  )
  const hasSidebar = useHasSidebar()

  const { data: teamWishlist, isPending } = useTeamWishlist(
    team.id,
    difficulty,
    selectedSeason.id,
    configurationId === 'all' ? undefined : parseInt(configurationId),
    source,
  )

  const visibleMembers = useMemo(() => {
    return (
      team.members?.filter((member) => member.teamRank?.wishlist_visibility) ??
      []
    )
  }, [team.members])

  const itemOptions = useMemo(() => {
    if (!teamWishlist) return []
    const byId = new Map<
      number,
      { name: string; quality: string; encounterName: string }
    >()
    teamWishlist.encounters.forEach((encounter) => {
      encounter.items.forEach((item) => {
        if (!byId.has(item.id)) {
          byId.set(item.id, {
            name: item.name,
            quality: item.quality,
            encounterName: encounter.name,
          })
        }
      })
    })
    return [...byId.entries()].map(([id, item]) => ({
      value: String(id),
      sortLabel: item.name,
      description: item.encounterName,
      label: (
        <span className='flex items-center gap-2'>
          <ItemIcon id={id} className='h-5 w-5' />
          <span className={`text-quality-${item.quality}`}>{item.name}</span>
        </span>
      ),
    }))
  }, [teamWishlist])

  const selectItem = (itemId: number) => {
    setView('encounters')
    localStorage.setItem('wishlistOverviewView', 'encounters')
    setListHash(null)
    setSelectedItemId(itemId)
    history.replaceState({}, '', `#item-${itemId}`)
  }

  const clearItem = (nextHash: string) => {
    setSelectedItemId(undefined)
    setListHash(nextHash)
    history.replaceState({}, '', nextHash)
  }

  const backFromItem = () => {
    if (!teamWishlist || selectedItemId == null) {
      clearItem('#all')
      return
    }
    const encounter = teamWishlist.encounters.find((candidate) =>
      candidate.items.some((item) => item.id === selectedItemId),
    )
    clearItem(encounter ? `#${encounter.id}` : '#all')
  }

  const leaveItemDetail = () => {
    if (selectedItemId != null) backFromItem()
  }

  const tableCard = (
    <Card
      className={cn(
        'relative p-0 overflow-hidden',
        view === 'encounters' && 'mt-2',
      )}
    >
      <div className='flex items-center gap-3 px-4 py-1.5 border-b'>
        {teamWishlist ? (
          <div className='w-64 max-w-full shrink-0'>
            <Select
              className='w-full text-sm'
              style={{ height: '2rem' }}
              isSearchable
              isClearable
              minimumSearchLength={2}
              optionWidth='fit'
              placeholder='Search for an item...'
              value={
                selectedItemId != null ? String(selectedItemId) : undefined
              }
              setValue={(value) => {
                if (value) selectItem(Number(value))
                else backFromItem()
              }}
              options={itemOptions}
            />
          </div>
        ) : null}
        <div className='justify-center flex gap-2 grow uppercase font-medium'>
          <span className='opacity-70'>View by</span>{' '}
          <span
            onClick={() => {
              setView('encounters')
              localStorage.setItem('wishlistOverviewView', 'encounters')
            }}
            className={`cursor-pointer transition-opacity ${
              view === 'encounters'
                ? 'text-foreground'
                : 'opacity-30 hover:opacity-100'
            }`}
          >
            Encounter
          </span>
          <span className='opacity-70'>/</span>
          <span
            onClick={() => {
              if (selectedItemId != null) backFromItem()
              setView('characters')
              localStorage.setItem('wishlistOverviewView', 'characters')
            }}
            className={`cursor-pointer transition-opacity ${
              view === 'characters'
                ? 'text-foreground'
                : 'opacity-30 hover:opacity-100'
            }`}
          >
            Character
          </span>
        </div>

        <Clock
          className='cursor-pointer text-muted-foreground hover:text-foreground transition-colors'
          onClick={() => setViewingAge(true)}
        />
        <TeamAgeModal
          teamWishlist={teamWishlist}
          open={viewingAge}
          setOpen={setViewingAge}
        />
      </div>

      {!teamWishlist ? (
        <p className='px-4 pb-4'>No wishlist data available</p>
      ) : view === 'encounters' ? (
        <div className='px-4 pb-4'>
          <TeamWishlistEncounters
            teamWishlist={teamWishlist}
            selectedItemId={selectedItemId}
            listHash={listHash}
            onSelectItem={selectItem}
            onClearItem={clearItem}
          />
        </div>
      ) : (
        <TeamWishlistTable teamWishlist={teamWishlist} />
      )}
    </Card>
  )

  return (
    <div>
      <h2 className='flex justify-between items-center gap-4 flex-wrap'>
        <div className='flex items-center whitespace-nowrap'>
          Wishlist overview
        </div>
        <OptionBar onSeasonChange={leaveItemDetail}>
          {droptimizerConfigurations.length > 1 && (
            <Select
              className='w-50 max-w-50 min-w-50'
              value={configurationId.toString()}
              setValue={(value) => {
                if (value) {
                  setConfigurationId(value)
                  leaveItemDetail()
                }
              }}
              options={[
                { value: 'all', label: 'All styles (combined)' },
              ].concat(
                droptimizerConfigurations.map((configuration) => ({
                  value: configuration.id.toString(),
                  label: configuration.name,
                })),
              )}
            />
          )}
        </OptionBar>
      </h2>

      {visibleMembers.length === 0 ? (
        <NoMemberWarning className='my-6' section='loot' />
      ) : null}

      <RcLootCouncilCta />

      <div className='flex items-end gap-2'>
        <DifficultySelector onDifficultyChange={leaveItemDetail} />
        {(selectedSeason.keystone_dungeons || []).length > 0 && (
          <Select
            className='w-50 max-w-50 min-w-50'
            value={source}
            setValue={(value) => {
              const next = value === 'dungeon' ? 'dungeon' : 'raid'
              const params = new URLSearchParams(searchParams)
              if (next === 'raid') {
                params.delete('source')
              } else {
                params.set('source', next)
              }
              setSearchParams(params, { replace: true })
              leaveItemDetail()
            }}
            options={[
              { value: 'raid', label: 'Raid items', sortLabel: '1' },
              { value: 'dungeon', label: 'Dungeon items', sortLabel: '2' },
            ]}
          />
        )}
      </div>

      {isPending ? (
        <Skeleton className='h-160 mt-2' />
      ) : view === 'characters' ? (
        <div
          className={cn(
            'root-width-breakout mt-2',
            hasSidebar && 'root-width-breakout-sidebar',
          )}
        >
          {tableCard}
        </div>
      ) : (
        tableCard
      )}
    </div>
  )
}

export default TeamOverview
